// src/components/RepresentationList.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import {
  fetchRepresentations,
  deleteRepresentation,
} from "../actions/representationActions";
import PaginationControls from "./PaginationControls";
import { formatNumber } from "../utils/numberFormat";
import { formatJalaliDate } from "../utils/dateFormat";
import { getFormErrors } from "../utils/formErrors";
import toast from "react-hot-toast";
import { toastApiError } from "../utils/toastErrors";
import {
  BadgeCheck,
  Loader2,
  Plus,
  Search,
  Pencil,
  Trash2,
  XCircle,
  CheckCircle2,
  Paperclip,
} from "lucide-react";

const cx = (...classes) => classes.filter(Boolean).join(" ");

const RepresentationList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    representations = [],
    loading,
    error,
    count = 0,
    next,
    previous,
  } = useSelector((state) => state.representations || {});

  const [searchText, setSearchText] = useState("");
  const [query, setQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const totalPages = Math.max(1, Math.ceil((count || 0) / pageSize));
  const hasNext = !!next;
  const hasPrevious = !!previous;

  // Debounce search
  useEffect(() => {
    const handler = setTimeout(() => {
      setQuery(searchText);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(handler);
  }, [searchText]);

  // Fetch when page/size/query changes
  useEffect(() => {
    dispatch(
      fetchRepresentations({
        page: currentPage,
        pageSize,
        search: query,
      })
    );
  }, [dispatch, currentPage, pageSize, query]);

  const refetch = useCallback(() => {
    dispatch(
      fetchRepresentations({
        page: currentPage,
        pageSize,
        search: query,
      })
    );
  }, [dispatch, currentPage, pageSize, query]);

  const handleDelete = async (id) => {
    if (!window.confirm("آیا مطمئن هستید؟")) return;
    try {
      await dispatch(deleteRepresentation(id));
      toast.success("وکالت‌نامه حذف شد.");
      refetch();
    } catch (err) {
      toastApiError(err, {}, "حذف وکالت‌نامه ناموفق بود.");
    }
  };

  const handleEditClick = (id) => navigate(`/representations/${id}/edit`);

  const pageStartIndex = useMemo(
    () => (currentPage - 1) * pageSize,
    [currentPage, pageSize]
  );

  const renderNames = (arr) =>
    (arr || [])
      .map((c) => c.full_name)
      .filter(Boolean)
      .join("، ") || "—";

  return (
    <div className="mt-24 px-3 sm:px-6 font-vazirmatn" dir="rtl">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white/95 backdrop-blur rounded-2xl shadow-lg border border-slate-200/80 overflow-hidden">
          {/* Header */}
          <div className="px-5 sm:px-7 py-5 border-b border-slate-200 bg-gradient-to-b from-slate-50 to-white">
            <div className="flex flex-col gap-3 sm:flex-row-reverse sm:items-center sm:justify-between">
              <Link
                to="/add-representation"
                className="inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold text-white bg-primary hover:opacity-95 active:opacity-90 transition shadow-sm"
              >
                <Plus size={18} />
                وکالتنامه جدید
              </Link>

              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-2xl bg-primary/10 text-primary flex items-center justify-center">
                  <BadgeCheck size={22} />
                </div>
                <div className="text-right">
                  <h2 className="text-lg sm:text-xl text-right font-extrabold text-slate-800">
                    لیست وکالت‌نامه‌ها
                  </h2>
                  <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
                    جستجو، مشاهده و مدیریت وکالت‌نامه‌ها
                    {typeof count === "number" ? (
                      <>
                        {" "}
                        • مجموع:{" "}
                        <span className="font-semibold text-slate-700">
                          {formatNumber(count)}
                        </span>
                      </>
                    ) : null}
                  </p>
                </div>
              </div>
            </div>

            {/* Search */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-slate-700 text-right mb-1.5">
                جستجو
              </label>
              <div className="relative">
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
                  <Search size={18} />
                </span>
                <input
                  type="text"
                  placeholder="متن جستجو را وارد کنید..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  className={cx(
                    "w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 pr-10 text-right text-slate-900",
                    "placeholder:text-slate-400 shadow-sm",
                    "focus:outline-none focus:ring-2 focus:ring-primary/25 focus:border-primary"
                  )}
                />
              </div>
            </div>
          </div>

          {/* Top Pagination */}
          <div className="px-5 sm:px-7 py-4 border-b border-slate-200 bg-white">
            <PaginationControls
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              pageSizeOptions={[5, 10, 20, 50, 100]}
              hasNext={hasNext}
              hasPrevious={hasPrevious}
              onPageChange={(e) => setCurrentPage(Number(e.target.value))}
              onNextPage={() => hasNext && setCurrentPage((c) => c + 1)}
              onPreviousPage={() => hasPrevious && setCurrentPage((c) => c - 1)}
              onPageSizeChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
            />
          </div>

          {/* Body */}
          <div className="px-5 sm:px-7 py-6">
            {loading ? (
              <div className="flex items-center justify-center gap-2 text-slate-600">
                <Loader2 className="animate-spin" size={18} />
                در حال بارگذاری...
              </div>
            ) : error ? (
              <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-right text-rose-700 text-sm">
                {getFormErrors({ response: { data: error } }, [], "خطا در دریافت وکالت‌نامه‌ها").general}
              </div>
            ) : representations.length === 0 ? (
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-right text-slate-700 text-sm">
                هیچ وکالت‌نامه‌ای یافت نشد.
              </div>
            ) : (
              <>
              <div className="space-y-3 lg:hidden">
                {representations.map((rep) => (
                  <article key={rep.id} aria-label={`وکالت‌نامه ${rep.doc_number || rep.id}`} className="min-w-0 rounded-xl border border-slate-200 p-4">
                    <h3 className="font-bold text-slate-900">وکالت‌نامه <bdi dir="ltr" className="break-all">{rep.doc_number || rep.id}</bdi></h3>
                    <dl className="mt-3 grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-2 text-sm">
                      <dt className="text-slate-500">وکیل</dt><dd className="break-words">{renderNames(rep.attorney)}</dd>
                      <dt className="text-slate-500">موکل</dt><dd className="break-words">{renderNames(rep.principal)}</dd>
                      <dt className="text-slate-500">درخواست‌دهنده</dt><dd className="break-words">{rep.applicant_info?.full_name || "—"}</dd>
                      <dt className="text-slate-500">تاریخ شروع</dt><dd><bdi dir="ltr">{formatJalaliDate(rep.start_date)}</bdi></dd>
                      <dt className="text-slate-500">تاریخ پایان</dt><dd><bdi dir="ltr">{formatJalaliDate(rep.end_date)}</bdi></dd>
                      <dt className="text-slate-500">توکیل به غیر</dt><dd>{rep.another_deligation ? "بله" : "خیر"}</dd>
                      <dt className="text-slate-500">عزل وکیل</dt><dd>{rep.representor_dismissal ? "بله" : "خیر"}</dd>
                    </dl>
                    {rep.representation_summary && <details className="mt-3 rounded-lg bg-slate-50 p-3 text-sm"><summary className="cursor-pointer font-medium">خلاصه وکالت‌نامه</summary><p className="mt-2 break-words leading-7">{rep.representation_summary}</p></details>}
                    <div className="mt-4 flex flex-wrap gap-2 border-t border-slate-100 pt-3">
                      <button type="button" onClick={() => handleEditClick(rep.id)} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm"><Pencil size={16} />ویرایش</button>
                      <button type="button" onClick={() => handleDelete(rep.id)} className="inline-flex items-center gap-2 rounded-lg border border-rose-200 px-3 py-2 text-sm text-rose-700"><Trash2 size={16} />حذف</button>
                      {rep.file ? <a href={rep.file} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm"><Paperclip size={16} />مشاهده فایل</a> : <span className="px-2 py-2 text-xs text-slate-500">بدون فایل</span>}
                    </div>
                  </article>
                ))}
              </div>
              <div className="hidden w-full overflow-auto rounded-2xl border border-slate-200 lg:block">
                <table className="w-full min-w-[1100px] text-sm bg-white">
                  <thead className="bg-slate-50 text-slate-700">
                    <tr className="border-b border-slate-200">
                      <th className="px-3 py-3 text-right font-semibold">
                        ردیف
                      </th>
                      <th className="px-3 py-3 text-right font-semibold">
                        وکیل
                      </th>
                      <th className="px-3 py-3 text-right font-semibold">
                        موکل
                      </th>
                      <th className="px-3 py-3 text-right font-semibold">
                        درخواست‌دهنده
                      </th>
                      <th className="px-3 py-3 text-right font-semibold">
                        تاریخ شروع
                      </th>
                      <th className="px-3 py-3 text-right font-semibold">
                        تاریخ پایان
                      </th>
                      <th className="px-3 py-3 text-right font-semibold">
                        توکل به غیر
                      </th>
                      <th className="px-3 py-3 text-right font-semibold">
                        عزل وکیل
                      </th>
                      <th className="px-3 py-3 text-right font-semibold">
                        خلاصه
                      </th>
                      <th className="px-3 py-3 text-right font-semibold">
                        فایل
                      </th>
                      <th className="px-3 py-3 text-right font-semibold">
                        عملیات
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {representations.map((rep, index) => (
                      <tr
                        key={rep.id}
                        className="border-b border-slate-100 hover:bg-slate-50/60"
                      >
                        <td className="px-3 py-3 text-slate-700">
                          {formatNumber(pageStartIndex + index + 1)}
                        </td>

                        <td className="px-3 py-3 text-slate-900 font-medium">
                          {renderNames(rep.attorney)}
                        </td>

                        <td className="px-3 py-3 text-slate-700">
                          {renderNames(rep.principal)}
                        </td>

                        <td className="px-3 py-3 text-slate-700">
                          {rep.applicant_info?.full_name || "-"}
                        </td>

                        <td className="px-3 py-3 text-slate-700">
                          <bdi dir="ltr" className="whitespace-nowrap">{formatJalaliDate(rep.start_date)}</bdi>
                        </td>

                        <td className="px-3 py-3 text-slate-700">
                          <bdi dir="ltr" className="whitespace-nowrap">{formatJalaliDate(rep.end_date)}</bdi>
                        </td>

                        <td className="px-3 py-3">
                          {rep.another_deligation ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-emerald-700">
                              <CheckCircle2 size={16} />
                              بله
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2.5 py-1 text-rose-700">
                              <XCircle size={16} />
                              خیر
                            </span>
                          )}
                        </td>

                        <td className="px-3 py-3">
                          {rep.representor_dismissal ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-emerald-700">
                              <CheckCircle2 size={16} />
                              بله
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2.5 py-1 text-rose-700">
                              <XCircle size={16} />
                              خیر
                            </span>
                          )}
                        </td>

                        <td className="px-3 py-3 text-slate-700 max-w-[420px]">
                          <div className="line-clamp-2">
                            {rep.representation_summary || "-"}
                          </div>
                        </td>

                        <td className="px-3 py-3">
                          {rep.file ? (
                            <a
                              href={rep.file}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-slate-700 hover:bg-slate-50"
                            >
                              <Paperclip size={16} />
                              مشاهده
                            </a>
                          ) : (
                            <span className="text-slate-400">بدون فایل</span>
                          )}
                        </td>

                        <td className="px-3 py-3">
                          <div className="flex items-center justify-start gap-2">
                            <button
                              type="button"
                              onClick={() => handleEditClick(rep.id)}
                              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-slate-700 hover:bg-slate-50"
                            >
                              <Pencil size={16} />
                              ویرایش
                            </button>

                            <button
                              type="button"
                              onClick={() => handleDelete(rep.id)}
                              className="inline-flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-1.5 text-rose-700 hover:bg-rose-100"
                            >
                              <Trash2 size={16} />
                              حذف
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              </>
            )}
          </div>

          {/* Bottom Pagination */}
          <div className="px-5 sm:px-7 py-4 border-t border-slate-200 bg-white">
            <PaginationControls
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              pageSizeOptions={[5, 10, 20, 50, 100]}
              hasNext={hasNext}
              hasPrevious={hasPrevious}
              onPageChange={(e) => setCurrentPage(Number(e.target.value))}
              onNextPage={() => hasNext && setCurrentPage((c) => c + 1)}
              onPreviousPage={() => hasPrevious && setCurrentPage((c) => c - 1)}
              onPageSizeChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default RepresentationList;
